<template>
  <App class="uav-project-view">
    <AppBar>
      <div class="flex-wrap">
        <BaseIcon icon="Building-" size="25" color="var(--primary)" style="margin-right:10px" />
        <span class="uav-view-bar-title">全景预览</span>
      </div>
      <div class="flex-wrap">
        <ButtonGroup size="small">
          <Button @click="RouteTo(`/core/uav-projects/${projectId}/editor`)">返回设计</Button>
          <Button @click="RouteTo('/core/uav-project')">项目列表</Button>
        </ButtonGroup>
      </div>
      <div class="flex-wrap split5"></div>
    </AppBar>

    <AppMask v-if="!is_ready" />

    <AppLeft class="uav-view-left" :collapse="false" :width="trailPanelWidth">
      <div class="uav-view-trail">
        <div class="uav-view-trail-title">路径</div>
        <div class="uav-view-trail-list">
          <a class="uav-view-trail-item" @click="RouteTo('/core/uav-project')">
            <span class="uav-view-trail-dot"></span>
            <span class="uav-view-trail-label">全景720场景设计</span>
          </a>
          <a
            v-if="project && project.id"
            class="uav-view-trail-item"
            @click="RouteTo(`/core/uav-projects/${project.id}/editor`)"
          >
            <span class="uav-view-trail-line"></span>
            <span class="uav-view-trail-dot"></span>
            <span class="uav-view-trail-label">{{ project.name }}</span>
          </a>
          <div class="uav-view-trail-item is-current">
            <span class="uav-view-trail-line"></span>
            <span class="uav-view-trail-dot is-current"></span>
            <span class="uav-view-trail-label">预览</span>
          </div>
        </div>
      </div>
    </AppLeft>

    <AppClient
      :collapse-left="true"
      :collapse-right="false"
      :left-width="trailPanelWidth"
      class="uav-view-client"
    >
      <div ref="viewBody" class="uav-view-body">
        <UAVViewer
          ref="viewer"
          :project-id="projectId"
          :use-public-api="usePublicApi"
          :fullscreen="false"
          controls-position="side-left"
          :show-subtitle="true"
          :show-photo-btn="true"
          :enable-annotation="true"
          :show-sandbox="true"
          :use-icons="true"
          :float-header="true"
        />
      </div>
    </AppClient>
  </App>
</template>

<script>
import { mapGetters } from 'vuex';
import App from '@/components/app/app';
import AppBar from '@/components/app/bar';
import AppMask from '@/components/app/mask';
import AppLeft from '@/components/app/left';
import AppClient from '@/components/app/client';
import UAVViewer from '@/components/uav/viewer.vue';

const TRAIL_PANEL_WIDTH = 220;

export default {
  name: 'UAVProjectView',
  routerOption: {
    as_object: true,
  },
  metaInfo: {
    title: '全景照片',
  },
  components: {
    App,
    AppBar,
    AppMask,
    AppLeft,
    AppClient,
    UAVViewer,
  },
  data() {
    return {
      trailPanelWidth: TRAIL_PANEL_WIDTH,
      viewResizeObserver: null,
    };
  },
  computed: {
    ...mapGetters('uav-project', ['is_ready', 'project']),
    projectId() {
      return this.$route.params.id;
    },
    usePublicApi() {
      try {
        const session = this.$store.getters['session/session'];
        return !(session && session.id);
      } catch (error) {
        return true;
      }
    },
  },
  watch: {
    $route: {
      handler() {
        this.loadProject();
      },
      immediate: true,
    },
  },
  mounted() {
    this.setupViewResizeObserver();
  },
  beforeDestroy() {
    if (this.viewResizeObserver) {
      this.viewResizeObserver.disconnect();
      this.viewResizeObserver = null;
    }
  },
  methods: {
    loadProject() {
      if (!this.projectId) return;
      this.$api.afterLogin(() => {
        this.$store.dispatch('uav-project/loadProject', this.projectId);
      });
    },
    setupViewResizeObserver() {
      const viewBody = this.$refs.viewBody;
      if (!viewBody || typeof ResizeObserver === 'undefined') {
        return;
      }
      this.viewResizeObserver = new ResizeObserver(() => {
        this.refreshViewerLayout();
      });
      this.viewResizeObserver.observe(viewBody);
    },
    refreshViewerLayout() {
      const viewer = this.$refs.viewer;
      const panorama = viewer && viewer.$refs.panorama;
      if (panorama && typeof panorama.onWindowResize === 'function') {
        panorama.onWindowResize();
      }
    },
  },
};
</script>

<style scoped lang="less">
.uav-project-view {
  height: 100%;
}

.uav-view-bar-title {
  color: var(--text2);
  font-size: 14px;
}

.uav-view-left {
  z-index: 120;
}

.uav-view-trail {
  height: 100%;
  padding: 16px 14px;
  background: var(--bg2);
  border-right: 1px solid var(--border);
  overflow-y: auto;
}

.uav-view-trail-title {
  margin-bottom: 14px;
  color: var(--subtext);
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.uav-view-trail-list {
  display: flex;
  flex-direction: column;
}

.uav-view-trail-item {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 8px 0 8px 2px;
  color: var(--text2);
  cursor: pointer;
  transition: color 0.2s ease;

  &.is-current {
    cursor: default;
    color: var(--primary);
    font-weight: 600;
  }

  &:not(.is-current):hover {
    color: var(--primary);
  }
}

.uav-view-trail-line {
  position: absolute;
  left: 6px;
  top: -8px;
  width: 1px;
  height: 16px;
  background: var(--border);
}

.uav-view-trail-dot {
  flex-shrink: 0;
  width: 12px;
  height: 12px;
  margin-top: 3px;
  border: 2px solid var(--border);
  border-radius: 50%;
  background: var(--bg2);

  &.is-current {
    border-color: var(--primary);
    background: var(--primary);
  }
}

.uav-view-trail-label {
  line-height: 1.5;
  word-break: break-word;
}

.uav-view-client {
  background: #0a0a0a;
  overflow: hidden;
}

.uav-view-body {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 0;
  overflow: hidden;
}
</style>
